import React, { useState, useEffect, useRef } from "react";
import Snackbar from "@mui/material/Snackbar";
import { Alert, Box } from "@mui/material";

const CustomSnackbar = ({ message, isOpen, onClose, type = "success" }) => {
  const [snackbarOpen, setSnackbarOpen] = useState(isOpen);
  const progressRef = useRef(null);
  const autoDuration = 8000;
  const timerRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const remainingTimeRef = useRef(autoDuration);
  const startTimeRef = useRef(null);

  // Type configuration
  const typeConfig = {
    success: {
      severity: "success",
      backgroundColor: "#55B938",
      progressColor: "#CCEAC4",
      shadowColor: "rgba(76, 175, 80, 0.2)"
    },
    error: {
      severity: "error",
      backgroundColor: "#d32f2f",
      progressColor: "#ffcdd2",
      shadowColor: "rgba(211, 47, 47, 0.2)"
    },
    info: {
      severity: "error",
      backgroundColor: "#5395ceff",
      progressColor: "#dadaedff",
      shadowColor: "rgba(211, 47, 47, 0.2)"
    }
  };

  const currentConfig = typeConfig[type] || typeConfig.success;

  useEffect(() => {
    setSnackbarOpen(isOpen);
  }, [isOpen]);

  useEffect(() => {
    if (snackbarOpen && !hovered) {
      startTimeRef.current = Date.now();

      if (progressRef.current) {
        progressRef.current.style.transition = 'none';
        progressRef.current.style.width = '100%';
        void progressRef.current.offsetHeight;
      }

      requestAnimationFrame(() => {
        if (progressRef.current) {
          progressRef.current.style.transition = `width ${remainingTimeRef.current}ms linear`;
          progressRef.current.style.width = '0%';
        }
      });

      timerRef.current = setTimeout(() => {
        handleCloseSnackbar();
      }, remainingTimeRef.current);
    }

    return () => clearTimeout(timerRef.current);
  }, [snackbarOpen, hovered]);

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
    onClose();
  };

  const handleMouseEnter = () => {
    setHovered(true);
    clearTimeout(timerRef.current);
    if (progressRef.current) {
      const elapsed = Date.now() - startTimeRef.current;
      remainingTimeRef.current = remainingTimeRef.current - elapsed;
      progressRef.current.style.transition = 'none';
    }
  };

  const handleMouseLeave = () => {
    setHovered(false);
  };

  return (
    <Snackbar
      open={snackbarOpen}
      autoHideDuration={autoDuration}
      onClose={handleCloseSnackbar}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      TransitionProps={{
        appear: true,
        timeout: 400
      }}
      sx={{
        "& .MuiSnackbar-root": {
          maxWidth: "90%"
        }
      }}
    >
      <Box
       onMouseEnter={handleMouseEnter}
       onMouseLeave={handleMouseLeave}
       sx={{ position: 'relative', borderRadius: '12px' }}>
        <Alert
          onClose={handleCloseSnackbar}
          severity={currentConfig.severity}
          sx={{
            width: "100%",
            borderRadius: "12px",
            boxShadow: `0 6px 20px ${currentConfig.shadowColor}`,
            fontWeight: 500,
            transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
            transform: snackbarOpen ? "translateY(0)" : "translateY(-20px)",
            opacity: snackbarOpen ? 1 : 0,
            backgroundColor: currentConfig.backgroundColor,
            display: "flex",
            color: "#fff",
            alignItems: "center",
            "& .MuiAlert-icon": {
              fontSize: "1.2rem",
              marginRight: "12px",
              display: "flex",
              alignItems: "center",
              color: "#fff",
              flexShrink: 0
            },
            "& .MuiAlert-message": {
              fontSize: "0.95rem",
              padding: "8px 0",
              letterSpacing: "0.01em",
              flex: 1,
            },
            "& .MuiAlert-action": {
              padding: "4px 12px 4px 8px",
              marginLeft: "8px",
              display: "flex",
              alignItems: "center",
              alignSelf: "stretch",
              flexShrink: 0,
              "& .MuiSvgIcon-root": {
                opacity: 0.9,
                transition: "all 0.2s ease",
                fontSize: "1.1rem",
                color: "#fff !important",
                "&:hover": {
                  opacity: 1,
                  transform: "scale(1.1)",
                },
              },
              "& .MuiButtonBase-root": {
                padding: "4px",
                marginRight: "-4px",
                color: "#fff !important",
              }
            },
          }}
        >
          {message}
        </Alert>
        
        {/* Progress indicator */}
        <Box
          ref={progressRef}
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            height: '3px',
            width: '100%',
            backgroundColor: currentConfig.progressColor,
            borderBottomLeftRadius: '12px',
            borderBottomRightRadius: '12px',
          }}
        />
      </Box>
    </Snackbar>
  );
};

export default CustomSnackbar;